import React, { useContext, useEffect, useMemo, useState } from 'react';
import { SET_NAME, EFF, Unit, Rune, runesFromAccount, unitsFromAccount, statsWith, setCounts, artifactEffectText } from './runeEngine';
import MonsterStatPanel from './MonsterStatPanel';
import StatRuneSwitch, { MergedPanel } from './StatRuneSwitch';
import RuneBuildPanel from './RuneBuildPanel';
import { preloadRuneImages } from './RuneStar';
import { useBackHandler } from '../hooks/useBackHandler';

/**
 * **공략에 적는 몹 세팅** — 룬을 하나하나 끼우는 게 아니라 **세트와 능력치 수치**를 적는다(사용자 지시).
 *
 * 능력치는 게임 몹 창에 보이는 **합계**(기본 + 룬 + 아티팩트)다. 읽는 사람은 "이 몹을 속도 몇에 치피
 * 몇으로 맞췄구나" 를 보면 되고, 쓰는 사람은 게임 화면을 보고 그대로 옮기면 된다.
 *
 * 전투기록에서 공략으로 불러오면 그 전투의 리플레이 룬으로 수치를 셈해 미리 채운다(`buildFromReplayUnit`)
 * — 셈은 룬 옵티마이저와 같은 `runeEngine` 이다.
 */
export interface TacticBuild {
    /** 완성된 세트 (4세트는 한 번, 2세트는 벌 수만큼 — 예: 폭주·의지 → [13, 15]) */
    sets: number[];
    hp: number; atk: number; def: number; spd: number;
    cr: number; cd: number; res: number; acc: number;
    /**
     * 전투기록에서 불러왔을 때 그 몹의 **룬 원본**(리플레이의 몹 한 마리 — 룬·아티팩트·유물).
     * 있으면 [룬 정보] 탭이 전투기록 창처럼 룬 별을 그린다. 수치를 손으로 고치면 원본과 안 맞으니 버린다.
     */
    raw?: any;
    /**
     * 아티팩트 부옵 — [속성 아티팩트, 타입 아티팩트] (효과 번호 → 값 — 시뮬 엔진이 추가 피해·치피 등에 쓴다). 시뮬레이션 탭에서만 적는다(`BuildRunes withArt`).
     * 주옵(체력·공격력·방어력)은 능력치 합계에 이미 들어 있다.
     */
    arts?: ArtPair;
}

export const BUILD_STATS: Array<{ key: keyof Omit<TacticBuild, 'sets'>; label: string; short: string; pct: boolean; max: number }> = [
    { key: 'hp', label: '체력', short: '체', pct: false, max: 200000 },
    { key: 'atk', label: '공격력', short: '공', pct: false, max: 20000 },
    { key: 'def', label: '방어력', short: '방', pct: false, max: 20000 },
    { key: 'spd', label: '공격속도', short: '속', pct: false, max: 500 },
    { key: 'cr', label: '치명 확률', short: '치확', pct: true, max: 100 },
    { key: 'cd', label: '치명 피해', short: '치피', pct: true, max: 500 },
    { key: 'res', label: '효과 저항', short: '효저', pct: true, max: 100 },
    { key: 'acc', label: '효과 적중', short: '효적', pct: true, max: 100 }
];

/** 4개가 한 세트인 것 — 나머지는 2개 */
const FOUR = new Set([3, 5, 8, 10, 11, 13]);
/** 고르기 쉬운 순서 — 점령전에서 많이 쓰는 것부터. 무형·강화·저항은 뺐다(사용자 지시) */
const SET_ORDER = [13, 15, 3, 10, 24, 17, 11, 5, 8, 4, 2, 7, 6, 1, 14, 16, 18, 19, 20, 22];

export const emptyBuild = (): TacticBuild => ({ sets: [], hp: 0, atk: 0, def: 0, spd: 0, cr: 0, cd: 0, res: 0, acc: 0 });
export const hasBuild = (b?: TacticBuild | null) => !!b && (b.sets.length > 0 || BUILD_STATS.some((s) => (b[s.key] || 0) > 0));

/** 세트 줄임 — "폭주+의지" */
export const setsText = (sets: number[]) => sets.map((s) => SET_NAME[s] || `세트${s}`).join('+');

/** 룬 원본에서 그릴 때 필요한 것만 — 공략에 담을 크기를 줄인다 */
export function slimRaw(raw: any): any {
    if (!raw) return undefined;
    const pick = (o: any, keys: string[]) => Object.fromEntries(keys.filter((k) => o?.[k] !== undefined).map((k) => [k, o[k]]));
    return {
        ...pick(raw, ['unit_master_id', 'unit_level', 'class', 'attribute', 'con', 'atk', 'def', 'spd', 'resist', 'critical_rate', 'critical_damage', 'accuracy']),
        runes: (raw.runes || []).map((r: any) => pick(r, ['rune_id', 'slot_no', 'set_id', 'class', 'rank', 'extra', 'upgrade_curr', 'pri_eff', 'prefix_eff', 'sec_eff'])),
        artifacts: (raw.artifacts || []).map((a: any) => pick(a, ['rid', 'slot', 'type', 'attribute', 'unit_style', 'rank', 'level', 'pri_effect', 'sec_effects'])),
        relics: (raw.relics || []).map((r: any) => pick(r, ['rid', 'type', 'upgrade_curr', 'durability', 'pri_effect', 'sec_effect']))
    };
}

/**
 * **전투기록 리플레이의 몹 한 마리 → 공략 세팅.**
 *
 * 리플레이 원본(`raw`)은 SWEX 계정 JSON 의 `unit_list[]` 한 칸과 모양이 같아 옵티마이저 셈을 그대로 쓴다.
 * 아티팩트 주옵까지 넣는다. 원본이 없는 옛 기록은 null — 그때는 비워 두고 사람이 적는다.
 * 리플레이에는 치명 확률·치명 피해·효과 적중 기본값이 없어 몹 기본값(15·50·0)으로 본다.
 */
export function buildFromReplayUnit(raw: any): TacticBuild | null {
    if (!raw || !Array.isArray(raw.runes)) return null;
    try {
        const account = { unit_list: [{ ...raw, unit_id: raw.unit_id || 1 }] };
        const unit = unitsFromAccount(account)[0];
        if (!unit) return null;
        const runes = runesFromAccount(account).runes;
        const lines = statsWith(unit, runes, unit.artifacts);
        const total = (k: string) => {
            const l = lines.find((x) => x.key === k);
            return l ? Math.round(l.base + l.bonus) : 0;
        };
        // 무형은 다른 세트를 채우는 조각으로 센다
        const count = setCounts(runes);
        const sets: number[] = [];
        for (const [id, n] of Object.entries(count)) {
            const sid = Number(id);
            for (let i = 0; i < Math.floor(n / (FOUR.has(sid) ? 4 : 2)); i++) sets.push(sid);
        }
        sets.sort((a, b) => Number(FOUR.has(b)) - Number(FOUR.has(a)));
        return {
            sets,
            hp: total('hp'), atk: total('atk'), def: total('def'), spd: total('spd'),
            // 치명 확률은 100% 가 끝이다(게임 화면도 100) — 각성 기본 30% 를 채우니 101% 가 되는 몹이 있었다
            cr: Math.min(100, total('cr')), cd: total('cd'), res: total('res'), acc: total('acc'),
            raw: slimRaw(raw)
        };
    } catch {
        return null;
    }
}

/** 몹 칸 아래 작은 단추 — 적었든 아니든 "룬 설정" 으로 보인다(사용자 지시). 적었으면 색이 찬다 */
export function BuildChip({ build, onClick, compact = false }: { build?: TacticBuild | null; onClick?: () => void; compact?: boolean }) {
    const filled = hasBuild(build);
    return (
        <button
            type="button" onClick={onClick} title={filled ? '룬 세팅 고치기' : '룬 세팅 적기'}
            style={{
                padding: compact ? '1px 6px' : '2px 8px', borderRadius: 999,
                border: filled ? 'none' : '1px dashed #9aa5d6', cursor: onClick ? 'pointer' : 'default',
                background: filled ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff',
                color: filled ? '#fff' : '#667eea', fontSize: compact ? 9 : 10, fontWeight: 800, whiteSpace: 'nowrap'
            }}
        >룬 설정</button>
    );
}

/**
 * **몹의 기본 능력치** (6성 40레벨, 각성 형태) — 몹 표(`monster-mapping`)의 `stats`.
 *
 * 몹 창은 게임처럼 "기본 + 추가(초록)" 로 보여야 해서(사용자 지시), 적은 합계에서 이걸 빼 추가분을 낸다.
 * 2차 각성이 있으면 그 모습을 쓴다. 표에 없으면 null — 그때는 합계만 보인다.
 */
export interface BaseStats { hp: number; atk: number; def: number; spd: number; cr: number; cd: number; res: number; acc: number }
export function baseStatsOf(mapping: any, mob: any): BaseStats | null {
    const info = mob?.name && mob?.attribute ? mapping?.monsters?.[mob.name]?.[mob.attribute] : null;
    const st = (info?.SecondAwakened || info?.Awakened)?.stats;
    if (!st) return null;
    return {
        hp: Number(st.maxHp ?? st.hp) || 0, atk: Number(st.maxAttack ?? st.attack) || 0, def: Number(st.maxDefense ?? st.defense) || 0,
        spd: Number(st.speed) || 0, cr: Number(st.critRate ?? 15), cd: Number(st.critDamage ?? 50),
        res: Number(st.resistance ?? 15), acc: Number(st.accuracy ?? 0)
    };
}

/** 몹 이름·그림 — 사이트의 다른 화면과 같은 규칙(`utils/monster`) */
function lookOf(mapping: any, mob: any): { name: string; img: string | null } {
    const info = mob?.name && mob?.attribute ? mapping?.monsters?.[mob.name]?.[mob.attribute] : null;
    let name = mob?.name || '';
    const kor = (info?.aliases || []).filter((a: string) => /[가-힣]/.test(a)).sort((a: string, b: string) => a.length - b.length);
    if (kor.length) name = kor[0];
    else if (info?.aliases?.length) name = info.aliases[0];
    const path = (info?.SecondAwakened || info?.Awakened)?.filepath;
    return { name, img: path ? `/mobs/v2/${String(path).replace(/\\/g, '/')}` : null };
}
const ATTR_NO: Record<string, number> = { Water: 1, Fire: 2, Wind: 3, Light: 4, Dark: 5 };

/**
 * 몹 창에 실을 몹과 "룬" — 기본 능력치를 몹으로, (적은 합계 − 기본)을 룬 하나로 만든다. 그러면 옵티마이저의
 * 몹 창이 그대로 "기본 + 추가(초록)" 로 그린다. 세트 효과는 이미 합계에 들어 있으니 이 룬에는 세트가 없다.
 */
function asPanel(build: TacticBuild, base: BaseStats | null, mob: any): { unit: Unit; runes: Rune[] } {
    const b = base || { hp: 0, atk: 0, def: 0, spd: 0, cr: 0, cd: 0, res: 0, acc: 0 };
    const unit: Unit = {
        unitId: 1, unitMasterId: 0, level: 40, stars: 6, attribute: ATTR_NO[mob?.attribute] || 0,
        base: { hp: b.hp, atk: b.atk, def: b.def, spd: b.spd, critRate: b.cr, critDmg: b.cd, resist: b.res, accuracy: b.acc },
        artifacts: [], relics: []
    };
    const total: Record<number, number> = {
        [EFF.HP_FLAT]: build.hp - b.hp, [EFF.ATK_FLAT]: build.atk - b.atk, [EFF.DEF_FLAT]: build.def - b.def,
        [EFF.SPD]: build.spd - b.spd, [EFF.CRIT_RATE]: build.cr - b.cr, [EFF.CRIT_DMG]: build.cd - b.cd,
        [EFF.RESIST]: build.res - b.res, [EFF.ACCURACY]: build.acc - b.acc
    };
    const rune: Rune = {
        id: 0, set: 0, slot: 0, ancient: false, stars: 6, up: 15, quality: 5, sell: 0,
        main: [0, 0], prefix: null, subs: [], total, unitMasterId: null
    };
    return { unit, runes: [rune] };
}

/**
 * 입력 칸 — 몹 창 글씨 크기에 맞춘다. 숫자 칸(`type="number"`)의 위아래 화살표는 쓰지 않는다(사용자 지시) —
 * 글자 칸에 숫자만 받는다. `max` 를 넘으면 `max` 로 자른다.
 */
function StatInput({ value, onChange, isMobile, pct, max }: { value: number; onChange: (v: number) => void; isMobile: boolean; pct: boolean; max?: number }) {
    const f = isMobile ? 0.85 : 1;
    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            {!pct && <span>+</span>}
            <input
                type="text" inputMode="numeric" pattern="[0-9]*"
                value={value ? String(value) : ''} placeholder="0"
                onChange={(e) => {
                    const v = Math.round(Number(e.target.value.replace(/[^0-9]/g, '')) || 0);
                    onChange(max !== undefined ? Math.min(v, max) : v);
                }}
                style={{
                    width: (pct ? 56 : 84) * f, padding: `${2 * f}px ${6 * f}px`, borderRadius: 6,
                    border: '1px solid #7a4a22', background: '#1a0f08',
                    // 치확·효저·효적(끝이 100)이 다 찼으면 빨갛게
                    color: pct ? (max === 100 && value >= 100 ? '#ff5a5a' : '#fff') : '#7ee36a',
                    fontSize: 18 * f, fontWeight: 700, textAlign: 'right'
                }}
            />
            {pct && <span>%</span>}
        </span>
    );
}

/**
 * 아티팩트 둘 — [속성 아티팩트 부옵, 타입 아티팩트 부옵], 줄마다 [효과 번호, 값]. 엔진에는 둘을 합쳐 넘긴다(`mergeArts`).
 * 적은 차례 그대로 두고 같은 효과가 겹쳐도 그대로 (사용자 2026-10-20 "막 순서가 뒤바껴 · 중복 검사하지 말고 이동도 하지마" — 효과 번호 표로 두면 번호 순으로 다시 섞였다)
 */
export type ArtRows = Array<[number, number]>;
export type ArtPair = [ArtRows, ArtRows];

/**
 * 붙을 수 있는 부옵 (계정 아티팩트 1,915개에서 확인 — 2026-10-20). 고르기 순서는 많이 쓰는 추가 피해·치피부터.
 * 속성 아티팩트만: 속성 피해 주는/받는(300~309) · 행동 불능 중 공속(203). 타입 아티팩트만: 스킬별 치피·회복·효적 · 3/4스 치피 · 첫 타 치피(400~411).
 */
const ART_COMMON = [221, 218, 219, 220, 224, 222, 223, 204, 205, 206, 207, 226, 208, 209, 225, 210, 211, 212, 213, 214, 215, 216, 217, 200, 201, 202];
const ART_IDS: [number[], number[]] = [
    [...ART_COMMON.slice(0, 5), 300, 301, 302, 303, 304, 305, 306, 307, 308, 309, ...ART_COMMON.slice(5), 203],
    [...ART_COMMON.slice(0, 5), 410, 411, 400, 401, 402, 403, 407, 408, 409, 404, 405, 406, ...ART_COMMON.slice(5)]
];
const ART_TITLE = ['속성 아티팩트', '타입 아티팩트'];
/** 아티팩트 하나의 부옵 칸 수 */
const ART_MAX = 4;
/** 효과 이름 — 값 자리는 'x' (사용자 2026-10-20) */
const artName = (id: number) => artifactEffectText(id, 'x' as unknown as number);

/** 낀 아티팩트 원본(type 1 속성 · 2 타입)의 부옵을 둘로 */
export function artsOfRaw(raw: any): ArtPair {
    const out: ArtPair = [[], []];
    for (const a of raw?.artifacts || []) {
        const k = Number(a?.type) === 2 ? 1 : 0;
        for (const e of a?.sec_effects || []) {
            const id = Number(e?.[0]), v = Number(e?.[1]);
            if (id && Number.isFinite(v)) out[k].push([id, v]);
        }
    }
    return out;
}
/**
 * 합만 있을 때(학습 기록 등) — 속성 전용은 속성에, 타입 전용(400~411)은 타입에, 둘 다 붙는 것은 속성 칸(4개)이 찰 때까지 속성에 · 넘치면 타입에
 * (모두 속성에 넣으면 속성 아티팩트가 6줄이 됐다 — 2026-10-20)
 */
export function splitArt(art: Record<number, number>): ArtPair {
    const out: ArtPair = [[], []];
    const entries = Object.entries(art || {}).map(([k, v]) => [Number(k), Number(v)] as [number, number]).filter(([, v]) => v);
    const elemOnly = (id: number) => id === 203 || (id >= 300 && id <= 309);
    const typeOnly = (id: number) => id >= 400 && id <= 411;
    for (const e of entries) if (elemOnly(e[0])) out[0].push(e); else if (typeOnly(e[0])) out[1].push(e);
    for (const e of entries) {
        if (elemOnly(e[0]) || typeOnly(e[0])) continue;
        out[out[0].length < ART_MAX ? 0 : 1].push(e);
    }
    return out;
}
/** 둘을 합친 효과 번호 → 값 (같은 효과는 더한다) — 엔진이 받는 모양 */
export function mergeArts(p: ArtPair): Record<number, number> {
    const out: Record<number, number> = {};
    for (const rows of p) for (const [k, v] of rows) if (Number(v)) out[k] = (out[k] || 0) + Number(v);
    return out;
}

const artBox = (merged: boolean, f: number): React.CSSProperties => ({
    color: '#f4efe6', ...(merged ? {} : { padding: `${10 * f}px ${12 * f}px`, borderRadius: 14, background: 'linear-gradient(180deg, #2f1f16 0%, #23160f 100%)', border: '1px solid #5a3d24' })
});
const artHead = (f: number, k: number): React.CSSProperties => ({ fontSize: 14 * f, fontWeight: 800, color: '#f2b23a', margin: `${k ? 12 * f : 0}px 0 6px` });

/**
 * **아티팩트 부옵 적기** (사용자 2026-10-20 "아티팩트도 설정할 수 있게해" · "속성 아티팩트 타입 아티팩트 구분해서") —
 * 속성·타입 아티팩트마다 줄(효과 · 값 · 지우기) 최대 4개, 아래 [+ 부옵]. 고를 수 있는 효과는 그 아티팩트에 붙는 것만. 적은 차례 그대로 · 같은 효과 겹쳐도 됨.
 */
export function ArtEditor({ arts, onChange, isMobile }: { arts: ArtPair; onChange: (a: ArtPair) => void; isMobile: boolean }) {
    const f = isMobile ? 0.85 : 1;
    const merged = useContext(MergedPanel);
    const sel: React.CSSProperties = { flex: 1, minWidth: 0, padding: `${4 * f}px ${6 * f}px`, borderRadius: 6, border: '1px solid #7a4a22', background: '#1a0f08', color: '#f4efe6', fontSize: 13 * f, fontWeight: 700 };
    return (
        <div style={artBox(merged, f)}>
            {([0, 1] as const).map((k) => {
                const rows = arts[k];
                const put = (list: ArtRows) => onChange((k === 0 ? [list, arts[1]] : [arts[0], list]) as ArtPair);
                return (
                    <div key={k}>
                        <div style={artHead(f, k)}>{ART_TITLE[k]}</div>
                        {rows.map(([id, v], i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                                <select value={id} style={sel} onChange={(e) => {
                                    const nid = Number(e.target.value);
                                    put(rows.map(([x, y], j) => (j === i ? [nid, y] : [x, y]) as [number, number]));
                                }}>
                                    {ART_IDS[k].map((x) => <option key={x} value={x}>{artName(x)}</option>)}
                                </select>
                                <ArtNum v={v} f={f} onChange={(n) => put(rows.map(([x, y], j) => (j === i ? [x, n] : [x, y]) as [number, number]))} />
                                <button type="button" title="지우기" onClick={() => put(rows.filter((_, j) => j !== i))} style={{ border: 'none', background: 'transparent', color: '#b8a58c', fontSize: 16 * f, cursor: 'pointer', padding: '0 4px' }}>×</button>
                            </div>
                        ))}
                        {rows.length < ART_MAX && (
                            <button
                                type="button"
                                onClick={() => put([...rows, [ART_IDS[k][0], 0]])}
                                style={{ border: '1px dashed #9a7a52', borderRadius: 8, background: 'transparent', color: '#f2b23a', fontSize: 12 * f, fontWeight: 800, padding: `${4 * f}px ${10 * f}px`, cursor: 'pointer' }}
                            >+ 부옵</button>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

/**
 * 부옵 값 칸 — 소수 한 자리까지 (사용자 2026-10-21 "체력 추가 피해는 소수점도 적용 · 한자리까지" — 게임 "체력의 1.1%만큼").
 * 적는 중인 글자("1.")는 칸이 따로 들고 있어야 점이 안 지워진다
 */
function ArtNum({ v, f, onChange }: { v: number; f: number; onChange: (n: number) => void }) {
    const [text, setText] = useState(v ? String(v) : '');
    useEffect(() => { if ((Number(text) || 0) !== (v || 0)) setText(v ? String(v) : ''); }, [v]); // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <input
            type="text" inputMode="decimal" value={text} placeholder="0"
            onChange={(e) => {
                const m = e.target.value.replace(/[^0-9.]/g, '').match(/^\d*(\.\d?)?/);
                const t = m ? m[0] : '';
                setText(t);
                onChange(Number(t) || 0);
            }}
            style={{ width: 64 * f, padding: `${3 * f}px ${6 * f}px`, borderRadius: 6, border: '1px solid #7a4a22', background: '#1a0f08', color: '#7ee36a', fontSize: 15 * f, fontWeight: 700, textAlign: 'right' }}
        />
    );
}

/** 아티팩트 부옵 보기 — 손으로 적은 세팅(룬 원본 없음)일 때 몹 창 오른쪽 */
function ArtList({ arts, isMobile }: { arts: ArtPair; isMobile: boolean }) {
    const f = isMobile ? 0.85 : 1;
    const merged = useContext(MergedPanel);
    return (
        <div style={artBox(merged, f)}>
            {([0, 1] as const).map((k) => {
                const rows = arts[k].filter(([, v]) => Number(v));
                return (
                    <div key={k}>
                        <div style={artHead(f, k)}>{ART_TITLE[k]}</div>
                        {!rows.length && <div style={{ fontSize: 12 * f, color: '#b8a58c' }}>없음</div>}
                        {rows.map(([a, v], i) => <div key={i} style={{ fontSize: 13 * f, fontWeight: 600, lineHeight: 1.6 }}>{artifactEffectText(Number(a), Number(v))}</div>)}
                    </div>
                );
            })}
        </div>
    );
}

/** 세트 고르기 — 몹 창 머리 아래. 4세트 4자리·2세트 2자리, 여섯 자리를 넘지 않는다 */
function SetPicker({ sets, onChange, isMobile }: { sets: number[]; onChange: (s: number[]) => void; isMobile: boolean }) {
    const f = isMobile ? 0.85 : 1;
    const used = (xs: number[]) => xs.reduce((n, s) => n + (FOUR.has(s) ? 4 : 2), 0);
    const sort = (xs: number[]) => [...xs].sort((a, b) => Number(FOUR.has(b)) - Number(FOUR.has(a)));
    return (
        <div style={{ marginTop: 10 * f, paddingTop: 8 * f, borderTop: '1px solid #5a3d24' }}>
            <div style={{ fontSize: 12 * f, color: '#b8a58c', fontWeight: 700, marginBottom: 6 * f }}>
                룬 세트 ({used(sets)}/6 자리)
            </div>
            <div style={{ display: 'flex', gap: 5 * f, flexWrap: 'wrap' }}>
                {SET_ORDER.map((id) => {
                    const n = sets.filter((s) => s === id).length;
                    const click = () => {
                        // 없으면 넣고, 2세트는 자리가 남으면 한 벌 더, 그 밖에는 뺀다
                        if (n === 0 && used(sets) + (FOUR.has(id) ? 4 : 2) <= 6) onChange(sort([...sets, id]));
                        else if (n > 0 && !FOUR.has(id) && used(sets) + 2 <= 6) onChange(sort([...sets, id]));
                        else onChange(sets.filter((s) => s !== id));
                    };
                    return (
                        <button
                            key={id} type="button" onClick={click}
                            style={{
                                padding: `${2 * f}px ${9 * f}px`, borderRadius: 999, cursor: 'pointer', fontSize: 13 * f, fontWeight: 800,
                                border: n ? '1px solid #f2b23a' : '1px solid #5a3d24',
                                background: n ? '#4c2d15' : 'transparent', color: n ? '#f2b23a' : '#8a7a66'
                            }}
                        >{SET_NAME[id]}{n > 1 ? ` ×${n}` : ''}</button>
                    );
                })}
            </div>
        </div>
    );
}

/**
 * **몹 창 모양의 세팅 판** — 보기와 적기가 같은 화면이다(사용자 지시: 이 화면에서 설정).
 *
 * 보기: 기본(흰색) + 추가(초록), 세트는 이름 아래. 적기: 체력·공격력·방어력·공속은 **초록 칸에 추가분**을,
 * 퍼센트 스탯은 **합계**를 적는다 — 게임 몹 창에 보이는 그대로 옮겨 적으면 된다. 저장은 합계로 한다.
 */
export function BuildPanel({ build, mob, mapping, isMobile, editing = false, onChange }: {
    build: TacticBuild;
    mob: any;
    mapping: any;
    isMobile: boolean;
    editing?: boolean;
    onChange?: (b: TacticBuild) => void;
}) {
    const base = baseStatsOf(mapping, mob);
    const look = lookOf(mapping, mob);
    const { unit, runes } = asPanel(build, base, mob);
    const set = (k: keyof Omit<TacticBuild, 'sets' | 'raw'>, v: number) => onChange?.({ ...build, [k]: v, raw: undefined });
    const B = base || { hp: 0, atk: 0, def: 0, spd: 0, cr: 0, cd: 0, res: 0, acc: 0 };
    const KEY: Record<string, keyof BaseStats> = { hp: 'hp', atk: 'atk', def: 'def', spd: 'spd', cr: 'cr', cd: 'cd', res: 'res', acc: 'acc' };

    return (
        <MonsterStatPanel
            unit={unit} runes={runes} artifacts={[]}
            sets={editing ? undefined : build.sets}
            name={look.name} img={look.img} isMobile={isMobile}
            editCell={editing ? (key, pct) => {
                const k = KEY[key];
                if (!k) return null;
                // 퍼센트는 합계, 나머지는 추가분을 적는다
                const max = BUILD_STATS.find((x) => x.key === k)?.max;
                return pct
                    ? <StatInput isMobile={isMobile} pct max={max} value={build[k]} onChange={(v) => set(k, v)} />
                    : <StatInput isMobile={isMobile} pct={false} max={max !== undefined ? Math.max(0, max - B[k]) : undefined} value={Math.max(0, build[k] - B[k])} onChange={(v) => set(k, B[k] + v)} />;
            } : undefined}
            headerExtra={editing ? (
                <>
                    {!base && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#e08a6a', fontWeight: 700 }}>
                            몹 표에 기본 능력치가 없어 합계로 적습니다
                        </div>
                    )}
                    <SetPicker isMobile={isMobile} sets={build.sets} onChange={(sets) => onChange?.({ ...build, sets, raw: undefined })} />
                </>
            ) : undefined}
        />
    );
}

/**
 * **세팅 적는 창** (공략 쓰기·수정 화면에서 [룬] 단추를 누르면). 안은 몹 창 모양 그대로다(`BuildPanel`).
 * 비우고 저장하면 지운 것으로 본다.
 */
export function BuildEditor({ open, mob, mapping, build, onSave, onClose }: {
    open: boolean;
    mob: any;
    mapping: any;
    build?: TacticBuild | null;
    onSave: (b: TacticBuild | null) => void;
    onClose: () => void;
}) {
    const [draft, setDraft] = useState<TacticBuild>(emptyBuild());
    const base = baseStatsOf(mapping, mob);
    useEffect(() => {
        if (!open) return;
        // 새로 적을 때는 기본 능력치에서 시작한다 — 추가분 0
        setDraft(build ? { ...build, sets: [...build.sets] } : (base ? { ...emptyBuild(), ...base } : emptyBuild()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, build]);
    if (!open) return null;
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
    /** 기본 능력치 그대로(추가분 0)에 세트도 없으면 적은 게 없다 */
    const untouched = (b: TacticBuild) => !b.sets.length && (!base || BUILD_STATS.every((s) => b[s.key] === (base as any)[s.key]));

    return (
        <div
            onClick={onClose}
            style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}
        >
            <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(560px, 100%)', maxHeight: '92vh', overflowY: 'auto' }}>
                <BuildPanel build={draft} mob={mob} mapping={mapping} isMobile={isMobile} editing onChange={setDraft} />
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button
                        type="button" onClick={() => { onSave(null); onClose(); }}
                        style={{ padding: '11px 16px', borderRadius: 12, border: '1px solid #5a3d24', background: '#2f1f16', color: '#b8a58c', fontWeight: 800, cursor: 'pointer' }}
                    >지우기</button>
                    <button
                        type="button" onClick={onClose}
                        style={{ padding: '11px 16px', borderRadius: 12, border: '1px solid #5a3d24', background: '#2f1f16', color: '#b8a58c', fontWeight: 800, cursor: 'pointer' }}
                    >취소</button>
                    <button
                        type="button" onClick={() => { onSave(untouched(draft) || !hasBuild(draft) ? null : draft); onClose(); }}
                        style={{ flex: 1, padding: '11px 0', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', fontWeight: 800, cursor: 'pointer' }}
                    >저장</button>
                </div>
            </div>
        </div>
    );
}

/**
 * **[룬 정보] 탭 — 공략에 적은 몹 세팅을 전투기록 창처럼 보여 준다.**
 *
 * 룬 원본(`raw`)이 있으면(전투기록에서 불러온 공략) 전투기록 창과 **똑같이** 몹 창 + 룬 별 + 아티팩트·유물을
 * 그린다(같은 컴포넌트, 같은 셈). 손으로 수치만 적은 몹은 몹 창 모양(`BuildPanel`)에 기본 + 추가로 싣는다.
 *
 * 고칠 수 있는 사람(`onSave` 를 주면)은 이 화면에서 바로 적고 고친다(사용자 지시). 룬 원본이 있던 몹을 고치면
 * 원본과 안 맞게 되니 원본은 버리고 수치만 남는다.
 */
/** BuildRunes [룬 변경] 옆 단추 */
export interface ExtraAction { label: string; onClick: () => void; busy?: boolean; busyLabel?: string }

export function BuildRunes({ build, mob, mapping, isMobile, onSave, applyLabel = '저장', withArt = false, extraAction }: {
    build: TacticBuild | null;
    mob: any;
    mapping: any;
    isMobile: boolean;
    onSave?: (b: TacticBuild | null) => Promise<void> | void;
    /** 아티팩트 부옵(`build.arts`)도 적고 보인다 — 시뮬레이션 탭 */
    withArt?: boolean;
    /** 고친 것을 넘기는 단추 이름 — 게시글 수정 중에는 바로 저장되지 않으니 "적용" */
    applyLabel?: string;
    /** [룬 변경] 왼쪽에 같은 모양으로 붙는 단추(하나 또는 여럿) — 시뮬 탭 [룬 최적화] · [내 룬 자동할당] (사용자 2026-10-27 · 10-29). busyLabel 은 도는 동안 글자 */
    extraAction?: ExtraAction | ExtraAction[];
}) {
    const extras = (Array.isArray(extraAction) ? extraAction : extraAction ? [extraAction] : []);
    useEffect(() => { preloadRuneImages(); }, []);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState<TacticBuild | null>(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => { setEditing(false); setDraft(null); }, [build, mob]);
    // 뒤로 가기: 고치던 세팅을 [취소] 처럼 버린다
    useBackHandler(editing && !!draft, () => { setEditing(false); setDraft(null); });

    const fromRaw = useMemo(() => {
        if (!build?.raw || !Array.isArray(build.raw.runes) || !build.raw.runes.length) return null;
        try {
            const account = { unit_list: [{ ...build.raw, unit_id: 1 }] };
            const unit = unitsFromAccount(account)[0] || null;
            const runes = runesFromAccount(account).runes.sort((a, b) => a.slot - b.slot);
            return unit ? { unit, runes } : null;
        } catch {
            return null;
        }
    }, [build]);

    const look = lookOf(mapping, mob);
    const base = baseStatsOf(mapping, mob);
    const btn = (primary: boolean): React.CSSProperties => ({
        padding: '8px 16px', borderRadius: 10, fontWeight: 800, cursor: 'pointer', fontSize: isMobile ? 12 : 13,
        border: primary ? 'none' : '1px solid rgba(102,126,234,0.35)',
        background: primary ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#fff', color: primary ? '#fff' : '#667eea'
    });
    const startEdit = () => {
        setDraft(build ? { ...build, sets: [...build.sets] } : (base ? { ...emptyBuild(), ...base } : emptyBuild()));
        setEditing(true);
    };

    if (editing && draft) {
        return (
            <div style={{ width: '100%' }}>
                {withArt ? (
                    // 능력치 판 오른쪽에 아티팩트 부옵 (사용자 2026-10-20 "오른쪽에다 같이 표시해서 설정하고 추가할 수 있게")
                    <StatRuneSwitch isMobile={isMobile} runeLabel="아티"
                        stat={<BuildPanel build={draft} mob={mob} mapping={mapping} isMobile={isMobile} editing onChange={setDraft} />}
                        rune={<ArtEditor arts={draft.arts || [[], []]} isMobile={isMobile} onChange={(arts) => setDraft({ ...draft, arts, raw: undefined })} />} />
                ) : <BuildPanel build={draft} mob={mob} mapping={mapping} isMobile={isMobile} editing onChange={setDraft} />}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
                    <button type="button" style={btn(false)} onClick={() => { setEditing(false); setDraft(null); }}>취소</button>
                    <button
                        type="button" style={btn(true)} disabled={saving}
                        onClick={async () => {
                            setSaving(true);
                            try { await onSave?.(hasBuild(draft) ? draft : null); } finally { setSaving(false); }
                            setEditing(false);
                        }}
                    >{saving ? '저장 중…' : applyLabel}</button>
                </div>
            </div>
        );
    }

    const editButton = onSave ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
            {extras.map((x) => <button key={x.label} type="button" style={btn(false)} disabled={x.busy} onClick={x.onClick}>{x.busy ? x.busyLabel || '맞추는 중…' : x.label}</button>)}
            <button type="button" style={btn(false)} onClick={startEdit}>룬 변경</button>
        </div>
    ) : null;
    /** 스탯·룬 상자 안에 넣는 [룬 변경] — 금테 판의 [첫 스킬 정보] 와 같은 모양 (사용자 2026-10-22: 따로 두지 말고 상자에) */
    const actionStyle: React.CSSProperties = {
        border: '1.5px solid #caa052', borderRadius: 9, background: 'rgba(40,26,16,0.7)', color: '#ffd98a', whiteSpace: 'nowrap',
        fontSize: isMobile ? 11 : 12.5, fontWeight: 800, padding: isMobile ? '4px 9px' : '5px 12px', cursor: 'pointer'
    };
    const editAction = onSave ? (
        extras.length ? (
            <div style={{ display: 'flex', gap: isMobile ? 4 : 6 }}>
                {extras.map((x) => (
                    <button key={x.label} type="button" onClick={x.onClick} disabled={x.busy} style={{ ...actionStyle, cursor: x.busy ? 'wait' : 'pointer' }}>
                        {x.busy ? x.busyLabel || '맞추는 중…' : x.label}
                    </button>
                ))}
                <button type="button" onClick={startEdit} style={actionStyle}>룬 변경</button>
            </div>
        ) : <button type="button" onClick={startEdit} style={actionStyle}>룬 변경</button>
    ) : null;

    if (!mob) {
        return (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: '#999', fontSize: isMobile ? 12 : 14, fontWeight: 600 }}>
                빈 칸입니다. [공략] 탭에서 몹을 넣어 주세요.
            </div>
        );
    }

    if (!hasBuild(build)) {
        return (
            // 적지 않았다는 문구는 두지 않는다(사용자 지시) — 고칠 수 있는 사람에게는 [룬 변경] 단추만
            <div style={{ width: '100%' }}>
                {editButton}
            </div>
        );
    }

    if (fromRaw) {
        return (
            <div style={{ width: '100%' }}>
                <StatRuneSwitch
                    isMobile={isMobile} action={editAction}
                    stat={<MonsterStatPanel unit={fromRaw.unit} runes={fromRaw.runes} artifacts={fromRaw.unit.artifacts} name={look.name} img={look.img} isMobile={isMobile} />}
                    rune={
                        <RuneBuildPanel
                            runes={fromRaw.runes} objective="power" relic={fromRaw.unit.relics[0] || null}
                            artifacts={fromRaw.unit.artifacts} isMobile={isMobile} asIs initialPick={1}
                        />
                    }
                />
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            {withArt ? (
                <StatRuneSwitch isMobile={isMobile} runeLabel="아티" action={editAction}
                    stat={<BuildPanel build={build!} mob={mob} mapping={mapping} isMobile={isMobile} />}
                    rune={<ArtList arts={build?.arts || [[], []]} isMobile={isMobile} />} />
            ) : <BuildPanel build={build!} mob={mob} mapping={mapping} isMobile={isMobile} />}
            {!withArt && editButton}
        </div>
    );
}
